import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Lock, LockOpen, BookMarked } from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";

const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const ACAO_LABEL = { sugerir_categoria: "Classificar", ignorar: "Ignorar", transferencia: "Transferência" };
const ACAO_CLASS = { sugerir_categoria: "bg-blue-100 text-blue-700", ignorar: "bg-slate-200 text-slate-600", transferencia: "bg-indigo-100 text-indigo-700" };

export function RegrasTab() {
  const [regras, setRegras] = useState([]);
  const [loading, setLoading] = useState(true);
  const fetch = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/conciliacao/regras`).then(r => setRegras(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const toggle = async (r) => {
    try {
      await axios.put(`${API}/conciliacao/regras/${r.id}`, { ativa: !r.ativa });
      fetch();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro")); }
  };
  const excluir = async (r) => {
    if (!window.confirm(`Excluir a regra "${r.padrao_descricao}"?`)) return;
    try {
      await axios.delete(`${API}/conciliacao/regras/${r.id}`);
      toast.success("Regra excluída");
      fetch();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro")); }
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="regras-tab">
      <CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Padrão (texto normalizado)</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Ação</TableHead>
            <TableHead>Conta</TableHead>
            <TableHead>Categoria</TableHead>
            <TableHead className="text-center">Aplicada</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="w-[90px]">Ações</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">Carregando…</TableCell></TableRow>
            ) : regras.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400">
                <BookMarked className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                Nenhuma regra ainda. Ao ignorar, marcar transferência ou classificar uma linha na conciliação, o sistema oferece "aplicar sempre".
              </TableCell></TableRow>
            ) : regras.map(r => (
              <TableRow key={r.id} className={r.ativa ? "" : "opacity-50"} data-testid={`regra-row-${r.id}`}>
                <TableCell className="font-mono text-xs">{r.padrao_descricao}</TableCell>
                <TableCell className="text-xs">{r.tipo === "debito" ? "Saída" : r.tipo === "credito" ? "Entrada" : "Ambos"}</TableCell>
                <TableCell><Badge className={ACAO_CLASS[r.acao] || ""}>{ACAO_LABEL[r.acao] || r.acao}</Badge></TableCell>
                <TableCell className="text-xs">{r.banco_nome}</TableCell>
                <TableCell className="text-xs">{r.categoria_nome || "—"}</TableCell>
                <TableCell className="text-center font-mono text-xs">{r.vezes_aplicada || 0}×</TableCell>
                <TableCell>
                  <button onClick={() => toggle(r)} data-testid={`toggle-regra-${r.id}`}>
                    <Badge className={r.ativa ? "bg-green-100 text-green-700 cursor-pointer" : "bg-slate-100 text-slate-500 cursor-pointer"}>
                      {r.ativa ? "Ativa" : "Inativa"}
                    </Badge>
                  </button>
                </TableCell>
                <TableCell>
                  <Button size="sm" variant="ghost" className="text-red-500" onClick={() => excluir(r)} data-testid={`del-regra-${r.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export function PainelTab() {
  const [ano, setAno] = useState(String(new Date().getFullYear()));
  const [dados, setDados] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const fetch = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/conciliacao/painel?ano=${ano}`).then(r => setDados(r.data)).catch(() => toast.error("Erro ao carregar painel")).finally(() => setLoading(false));
  }, [ano]);
  useEffect(() => { fetch(); }, [fetch]);

  const fechar = async (bancoId, m) => {
    if (!window.confirm(`Fechar o período ${m.ano_mes}? Conciliações desse mês ficarão bloqueadas.`)) return;
    setBusy(`${bancoId}-${m.ano_mes}`);
    try {
      await axios.post(`${API}/conciliacao/painel/fechar`, { banco_id: bancoId, ano_mes: m.ano_mes, saldo_conciliado: m.saldo_extrato ?? m.saldo_final_sistema });
      toast.success(`Período ${m.ano_mes} fechado`);
      fetch();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao fechar")); }
    finally { setBusy(null); }
  };
  const reabrir = async (bancoId, m) => {
    if (!window.confirm(`Reabrir o período ${m.ano_mes}? (ação de administrador)`)) return;
    setBusy(`${bancoId}-${m.ano_mes}`);
    try {
      await axios.post(`${API}/conciliacao/painel/reabrir`, { banco_id: bancoId, ano_mes: m.ano_mes });
      toast.success(`Período ${m.ano_mes} reaberto`);
      fetch();
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao reabrir")); }
    finally { setBusy(null); }
  };

  const anos = []; const y = new Date().getFullYear();
  for (let a = y; a >= y - 4; a--) anos.push({ value: String(a), label: String(a) });

  return (
    <div className="space-y-4" data-testid="painel-tab">
      <div className="flex items-center gap-3">
        <NativeSelect value={ano} onValueChange={setAno} options={anos} className="w-28" data-testid="select-ano-painel" />
        {loading && <span className="text-sm text-slate-400">Carregando…</span>}
      </div>
      {(dados?.contas || []).map(({ banco, meses }) => (
        <Card key={banco.id} className="border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="font-semibold text-luvcorp-navy mb-2">
              {banco.nome} {banco.agencia && <span className="text-xs text-slate-400">Ag {banco.agencia} / CC {banco.conta || "-"}</span>}
            </p>
            {meses.length === 0 ? <p className="text-sm text-slate-400">Sem movimento no ano.</p> : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Mês</TableHead>
                    <TableHead className="text-right">Saldo Inicial</TableHead>
                    <TableHead className="text-right">Entradas</TableHead>
                    <TableHead className="text-right">Saídas</TableHead>
                    <TableHead className="text-right">Saldo Sistema</TableHead>
                    <TableHead className="text-right">Saldo Extrato</TableHead>
                    <TableHead className="text-right">Diferença</TableHead>
                    <TableHead className="text-center">% Conc.</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="w-[110px]"></TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {meses.map(m => (
                      <TableRow key={m.ano_mes} data-testid={`painel-${banco.id}-${m.ano_mes}`}>
                        <TableCell className="font-mono text-xs">{m.ano_mes.split("-").reverse().join("/")}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{fmtBRL(m.saldo_inicial)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-emerald-700">{fmtBRL(m.entradas)}</TableCell>
                        <TableCell className="text-right font-mono text-xs text-red-700">{fmtBRL(m.saidas)}</TableCell>
                        <TableCell className="text-right font-mono text-xs font-semibold">{fmtBRL(m.saldo_final_sistema)}</TableCell>
                        <TableCell className="text-right font-mono text-xs">{m.saldo_extrato != null ? fmtBRL(m.saldo_extrato) : "—"}</TableCell>
                        <TableCell className={`text-right font-mono text-xs font-bold ${m.diferenca === 0 ? "text-emerald-700" : m.diferenca != null ? "text-red-700" : "text-slate-400"}`}>
                          {m.diferenca != null ? fmtBRL(m.diferenca) : "—"}
                        </TableCell>
                        <TableCell className="text-center text-xs">{m.pct_conciliado != null ? `${m.pct_conciliado}%` : "—"}</TableCell>
                        <TableCell className="text-center">
                          {m.fechado ? <Badge className="bg-emerald-100 text-emerald-700">Fechado ✓</Badge>
                            : (m.linhas_total - m.linhas_resolvidas) > 0 ? <Badge className="bg-amber-100 text-amber-700">Pendências</Badge>
                            : <Badge className="bg-slate-100 text-slate-500">Aberto</Badge>}
                        </TableCell>
                        <TableCell>
                          {m.fechado ? (
                            <Button size="sm" variant="ghost" onClick={() => reabrir(banco.id, m)} disabled={busy === `${banco.id}-${m.ano_mes}`} data-testid={`btn-reabrir-${banco.id}-${m.ano_mes}`}>
                              <LockOpen className="h-3.5 w-3.5 mr-1" />Reabrir
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => fechar(banco.id, m)} disabled={busy === `${banco.id}-${m.ano_mes}`} data-testid={`btn-fechar-${banco.id}-${m.ano_mes}`}>
                              <Lock className="h-3.5 w-3.5 mr-1" />Fechar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
      {dados && dados.contas.length === 0 && <p className="text-slate-400 text-sm">Nenhuma conta com conciliação ativa.</p>}
    </div>
  );
}
